import React, { useCallback, useState } from 'react'
import { Main, MainBody, MainTop, WhiteBox } from '@/components/Layout'
import { FormLoader, PropertyRenderer } from '@/components/changeRequests'
import context from '@/context'
import { ValidationBoundary } from '@/components/validation'
import { TabContent, TabNavItems, TabPane } from '@/components/tabs'
import { AddressesInstitution } from '@/components/demographics/addresses'
import { Licenses } from '@/components/demographics/licenses'
import { InstitutionContacts } from '@/components/demographics/contacts'
import { InstitutionGroups } from '@/components/demographics/institutionGroups'
import { Branches } from '@/components/demographics/branches'
import { InstitutionRealEstates } from '@/components/demographics/realEstates'
import { AssociatedToInstitutions, RelatedPersons } from '@/components/demographics/relatedPersons'
import { ScopeDetailHeader } from '@/components/changeRequests/scopeDetailHeader'
import { getBadges } from '@/components/changeRequests/badgeUtils'
import { BankBasicDetails } from '@/components/demographics/bankBasicDetails'
import { IndividualsInCharge } from '@/components/demographics/individualsInCharge'
import { StatusHistory } from '@/components/demographics/statusHistory'
import { CapitalOperations } from '@/components/demographics/capitalOperations'
import { BankCapital } from '@/components/demographics/bankCapital'
import { BankAccountNumbers } from '@/components/demographics/bankAccountNumbers'
import { useLocalization } from '@/hooks/localization'
import { institutionPermissionTypes } from '@/enums/institutionPermissionTypes'
import { InstitutionBlacklists } from '@/components/demographics/blacklists'
import { CompanyDealsHistory } from '@/components/demographics/bankDealsHistory'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import { InstitutionChangeRequestHistory } from '@/components/demographics/changeRequestHistory'
import { useInstitutionPermissionChecker } from '@/hooks/useInstitutionPermissionsChecker'
import { usePropertyReader } from '@/hooks/usePropertyReader'
import PropTypes from 'prop-types'
import { permissionTypes } from '@/enums/permissionTypes'

const BankDetail = React.memo(function BankDetail (props) {
  const loader = useCallback(id => context.isInternalApp() ? context.api.bankService.getBankById(id) : context.api.bankService.get(), [])
  const renderForm = useCallback(formProps => (<BankDetailForm {...formProps} />), [])
  return <FormLoader {...props} loader={loader} render={renderForm} />
})

const BankDetailForm = React.memo(function BankDetailForm (props) {
  const { targetId, mode, data, isChangeItemId } = props
  const [institutionId] = usePropertyReader(['institutionId'])
  const { localizeProps } = useLocalization()
  const [activeTab, setActiveTab] = useState('1')
  const isGranted = usePermissionChecker()
  const getInstitutionPermission = useInstitutionPermissionChecker(institutionId, isChangeItemId)

  const getAddresses = useCallback(req => context.api.bankService.getAddresses(targetId, req), [targetId])
  const getLicenses = useCallback(req => context.api.bankService.getLicenses(targetId, req), [targetId])
  const getContacts = useCallback(req => context.api.bankService.getContacts(targetId, req), [targetId])
  const getInstitutionGroups = useCallback(req => context.api.bankService.getInstitutionGroups(targetId, req), [targetId])
  const getBranches = useCallback(req => context.api.bankService.getBranches(targetId, req), [targetId])
  const getRealEstates = useCallback(req => context.api.bankService.getRealEstates(targetId, req), [targetId])
  const getRealEstate = useCallback(id => context.api.bankService.getRealEstate(id), [])
  const getIndividualsInCharge = useCallback(req => context.api.bankService.getIndividualsInCharge(targetId, req), [targetId])
  const getRelatedPersons = useCallback(req => context.api.bankService.getRelatedPersons(targetId, req), [targetId])
  const getPersonsRelatedToThis = useCallback(req => context.api.bankService.getPersonsRelatedToThis(targetId, req), [targetId])

  const getStatusHistory = useCallback(req => context.api.bankService.getStatusHistory(targetId, req), [targetId])
  const getCompanyDealsHistory = useCallback(req => context.api.bankService.getCompanyDealsHistory(targetId, req), [targetId])
  const getChangeRequestHistory = useCallback(req => context.api.changeRequestService.getHistoryForEntity(targetId, 'Banks', req), [targetId])
  const getCapitalOperations = useCallback(req => context.api.bankService.getCapitalOperations(targetId, req), [targetId])
  const getBankAccountNumbers = useCallback(req => context.api.bankService.getBankAccountNumbers(targetId, req), [targetId])

  const permissionPredicate = useCallback(x => !x.permission ||
    getInstitutionPermission(x.permission) !== permissionTypes.enum.None,
  [getInstitutionPermission])
  const hideReadonlyInInsertMode = useCallback(x => !x.readonly || targetId, [targetId])
  const canViewChangeHistory = isGranted('CanViewChangeHistory')
  return (
    <Main>
      <MainTop>
        <ScopeDetailHeader title='Institutions.BankDetailTitle' backUrl={context.isInternalApp() ? '/banks' : ''}>
          <PropertyRenderer name={localizeProps()} readonly />
        </ScopeDetailHeader>
      </MainTop>
      <MainBody transparent>
        <ValidationBoundary>
          <WhiteBox>
            <TabNavItems
              buttonView
              activeTabId={activeTab}
              onClick={setActiveTab}
              items={getBadges(mode, data, [
                { id: '1', text: 'Institutions.Details', badge: '', exclude: '^(bankCapital|hasBankCapital)' },
                { id: '2', text: 'Persons.Addresses', badge: 'addresses', permission: institutionPermissionTypes.enum.Addresses },
                { id: '3', text: 'Institutions.Licenses', badge: 'licenses', permission: institutionPermissionTypes.enum.Licenses },
                { id: '4', text: 'Persons.Contacts', badge: 'contacts', permission: institutionPermissionTypes.enum.Contacts },
                context.isInternalApp() && { id: '5', text: 'Institutions.Groups', badge: 'groups', readonly: true, permission: institutionPermissionTypes.enum.InstitutionGroups },
                { id: '6', text: 'Institutions.Branches', badge: 'branches', permission: institutionPermissionTypes.enum.Branches },
                { id: '7', text: 'Institutions.RealEstates', badge: 'realEstates', permission: institutionPermissionTypes.enum.RealEstates },
                context.isInternalApp() && { id: '8', text: 'Institutions.IndividualsInCharge', badge: 'individualsInChange', readonly: true, permission: institutionPermissionTypes.enum.IndividualsInCharge },
                { id: '9', text: 'Institutions.RelatedPersons', badge: 'relatedPersons', readonly: !context.isInternalApp(), permission: institutionPermissionTypes.enum.AssociatedPersons },
                { id: '10', text: 'Institutions.PersonsRelatedToThis', readonly: true, permission: institutionPermissionTypes.enum.AssociatedToInstitutions },
                { id: '11', text: 'Institutions.StatusHistory', readonly: true, permission: institutionPermissionTypes.enum.StatusHistory },
                { id: '16', text: 'Institutions.CompanyDealsHistory', readonly: true, permission: institutionPermissionTypes.enum.BankDealsHistory },
                (canViewChangeHistory && !isChangeItemId) && { id: '17', text: 'Institutions.ChangeRequestHistory', badge: 'chrHistory', readonly: true, permission: institutionPermissionTypes.enum.ChangeHistory },
                { id: '12', text: 'Institutions.CapitalOperations', badge: 'bankCapitalOperations', permission: institutionPermissionTypes.enum.CapitalOperations },
                { id: '13', text: 'Institutions.BankCapitalTitle', badge: 'bankCapital', permission: institutionPermissionTypes.enum.BankCapital },
                { id: '14', text: 'Institutions.AccountNumbers', badge: 'bankAccountNumbers', permission: institutionPermissionTypes.enum.BankAccountNumbers },
                context.isInternalApp() && { id: '15', text: 'Blacklists.BlacklistTitle', badge: 'blacklists', readonly: true, permission: institutionPermissionTypes.enum.Blacklists }
              ].filter(permissionPredicate).filter(hideReadonlyInInsertMode).filter(Boolean))} />
            <TabContent activeTab={activeTab} renderActiveOnly>
              <TabPane tabId='1'>
                <BankBasicDetails />
              </TabPane>
              <TabPane tabId='2'>
                <AddressesInstitution dataSource={getAddresses} />
              </TabPane>
              <TabPane tabId='3'>
                <Licenses dataSource={getLicenses} />
              </TabPane>
              <TabPane tabId='4'>
                <InstitutionContacts dataSource={getContacts} />
              </TabPane>
              {context.isInternalApp() && <TabPane tabId='5' className='read-only'>
                <InstitutionGroups dataSource={getInstitutionGroups} />
              </TabPane>}
              <TabPane tabId='6'>
                <Branches dataSource={getBranches} />
              </TabPane>
              <TabPane tabId='7'>
                <InstitutionRealEstates dataSource={getRealEstates} detailDataSource={getRealEstate} />
              </TabPane>
              {context.isInternalApp() && <TabPane tabId='8'>
                <IndividualsInCharge dataSource={getIndividualsInCharge} />
              </TabPane>}
              <TabPane tabId='9'>
                <RelatedPersons nameField='person'
                  dataSource={getRelatedPersons}
                  readonly={!context.isInternalApp()}
                  permission={institutionPermissionTypes.enum.AssociatedPersons} />
              </TabPane>
              <TabPane tabId='10'>
                <AssociatedToInstitutions
                  nameField='institution'
                  dataSource={getPersonsRelatedToThis}
                  permission={institutionPermissionTypes.enum.AssociatedToInstitutions}
                  readonly
                />
              </TabPane>
              <TabPane tabId='11'>
                <StatusHistory dataSource={getStatusHistory} />
              </TabPane>
              <TabPane tabId='16'>
                <CompanyDealsHistory dataSource={getCompanyDealsHistory} />
              </TabPane>
              <TabPane tabId='17'>
                <InstitutionChangeRequestHistory dataSource={getChangeRequestHistory} />
              </TabPane>
              <TabPane tabId='12'>
                <CapitalOperations dataSource={getCapitalOperations} />
              </TabPane>
              <TabPane tabId='13'>
                <BankCapital />
              </TabPane>
              <TabPane tabId='14'>
                <BankAccountNumbers dataSource={getBankAccountNumbers} />
              </TabPane>
              {context.isInternalApp() && <TabPane tabId='15'>
                <InstitutionBlacklists />
              </TabPane>}
            </TabContent>
          </WhiteBox>
        </ValidationBoundary>
      </MainBody>
    </Main>
  )
})

BankDetailForm.propTypes = {
  targetId: PropTypes.number.isRequired,
  mode: PropTypes.string.isRequired,
  data: PropTypes.object,
  isChangeItemId: PropTypes.bool
}

export {
  BankDetail
}



// WEBPACK FOOTER //
// ./src/components/demographics/bankDetail.js